/**
 * Location picker with country and city suggestions
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, Globe, AlertCircle, Check } from 'lucide-react';
import { COUNTRIES, CITIES_BY_COUNTRY, validateLocation, ValidationError } from '@/utils/validation';

interface LocationPickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (countryCode: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  helpText?: string;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  label,
  value,
  onChange,
  onCountryChange,
  placeholder = 'Enter your location',
  disabled = false,
  required = false,
  className = '',
  helpText
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [error, setError] = useState<ValidationError | null>(null);
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Validation
  useEffect(() => {
    if (touched) {
      const validationError = validateLocation(value);
      setError(validationError);
      setIsValid(!validationError && value.trim() !== '');
    }
  }, [value, touched]);

  // Filter suggestions based on search query
  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCities = selectedCountry && CITIES_BY_COUNTRY[selectedCountry]
    ? CITIES_BY_COUNTRY[selectedCountry].filter(city =>
        city.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSearchQuery(newValue);
    setIsOpen(true);
  };

  // Handle input blur
  const handleBlur = () => {
    setTouched(true);
    // Delay closing to allow for clicks on dropdown
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 150);
  };

  // Handle focus
  const handleFocus = () => {
    setSearchQuery(value);
    setIsOpen(true);
  };

  // Handle country selection
  const handleCountrySelect = (country: typeof COUNTRIES[0]) => {
    setSelectedCountry(country.code);
    setSearchQuery('');
    onChange('');
    onCountryChange?.(country.code);
    inputRef.current?.focus();
  };

  // Handle city selection
  const handleCitySelect = (city: string) => {
    const countryName = COUNTRIES.find(c => c.code === selectedCountry)?.name || '';
    const locationValue = `${city}, ${countryName}`;
    onChange(locationValue);
    setSearchQuery('');
    setIsOpen(false);
  };

  // Handle custom location
  const handleCustomLocation = () => {
    onChange(searchQuery);
    setIsOpen(false);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputClasses = `
    w-full px-3 py-2 pl-10 rounded-lg bg-white dark:bg-gray-900 text-sm border 
    focus:outline-none focus:ring-1 transition-all duration-200
    ${error && touched 
      ? 'border-red-300 dark:border-red-700 focus:ring-red-300 dark:focus:ring-red-700' 
      : isValid && touched
      ? 'border-green-300 dark:border-green-700 focus:ring-green-300 dark:focus:ring-green-700'
      : 'border-gray-200 dark:border-gray-800 focus:ring-gray-300 dark:focus:ring-gray-700'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        {/* Input field */}
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            className={inputClasses}
            value={isOpen ? searchQuery : value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
          />
          
          {/* Validation icons */}
          {touched && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {error ? (
                <AlertCircle className="h-4 w-4 text-red-500" />
              ) : isValid ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : null}
            </div>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg max-h-80 overflow-y-auto"
          >
            {/* Country selection */}
            {!selectedCountry && (
              <div>
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    <Globe className="h-3 w-3" />
                    Select Country
                  </div>
                </div>
                {filteredCountries.slice(0, 10).map((country) => (
                  <div
                    key={country.code}
                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer text-sm flex items-center gap-2"
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                      {country.code}
                    </span>
                    {country.name}
                  </div>
                ))}
              </div>
            )}

            {/* City suggestions */}
            {selectedCountry && filteredCities.length > 0 && (
              <div>
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    <MapPin className="h-3 w-3" />
                    Popular Cities in {COUNTRIES.find(c => c.code === selectedCountry)?.name}
                  </div>
                </div>
                {filteredCities.slice(0, 8).map((city) => (
                  <div
                    key={city}
                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer text-sm"
                    onClick={() => handleCitySelect(city)}
                  >
                    {city}
                  </div>
                ))}
              </div>
            )}

            {/* Custom location option */}
            {searchQuery && searchQuery.trim() !== '' && (
              <div>
                {(selectedCountry || filteredCountries.length > 0) && (
                  <div className="border-t border-gray-200 dark:border-gray-800" />
                )}
                <div
                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer text-sm flex items-center gap-2"
                  onClick={handleCustomLocation}
                >
                  <Search className="h-3 w-3 text-gray-400" />
                  Use "{searchQuery}"
                </div>
              </div>
            )}

            {/* No results */}
            {searchQuery && filteredCountries.length === 0 && filteredCities.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No suggestions found
              </div>
            )}

            {/* Back to country selection */}
            {selectedCountry && (
              <div className="border-t border-gray-200 dark:border-gray-800">
                <div
                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer text-sm text-gray-500 dark:text-gray-400"
                  onClick={() => {
                    setSelectedCountry('');
                    setSearchQuery('');
                  }}
                >
                  ← Back to country selection
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && touched && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error.message}
        </p>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}

      {/* Selected country indicator */}
      {selectedCountry && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Globe className="h-3 w-3" />
          Selected country: {COUNTRIES.find(c => c.code === selectedCountry)?.name}
          <button
            onClick={() => setSelectedCountry('')}
            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
};
